// แปลงแถว DB → รูปทรงที่ Console วาด (ทรงเดียวกับ mockData เพื่อให้ MOCK/LIVE ใช้ UI ร่วมกัน)
import { reqLabel, reqCat, PHASE_OF_STATUS } from './reqtypes.js';
import { computeUrgency, daysUntil, sortByUrgency } from './urgency.js';

const HUES = ['#8b5cf6', '#2f6bff', '#f0930c', '#12b76a', '#00b3c7', '#ff5470'];
export const hueFor = (name = '') =>
  HUES[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % HUES.length];

const CARD_STATUS = (r) => {
  if (!r.required) return 'na';
  if (r.status === 'flagged') return 'f';
  if (r.status === 'received' || r.status === 'verified') return 'ok';
  return 'm';
};

// ลำดับเวลาจริงของ needed_by (string sort ผิด — before_clear จะมาก่อน before_pay)
const NEEDED_BY_RANK = {
  before_pay: 0, before_ship: 1, before_pickup: 2, before_clear: 3, on_arrival: 4,
};
const gapSort = (a, b) => (NEEDED_BY_RANK[a.needed_by] ?? 9) - (NEEDED_BY_RANK[b.needed_by] ?? 9);
const openGaps = (reqs) => reqs
  .filter(r => r.required && ['missing', 'flagged'].includes(r.status))
  .sort(gapSort);

export function etaText(s) {
  if (!s.eta) return 'ยังไม่กำหนด';
  const d = daysUntil(s.eta);
  if (d < 0) return `ถึงแล้ว ${-d} วัน`;
  if (d === 0) return 'ETA วันนี้';
  return `ETA ${d} วัน`;
}

// ช่องใบอนุญาตมีช่องเดียวต่อใบ แต่หลายรุ่นใช้ร่วมกัน ไฟล์เดียวไม่ได้แปลว่าครอบทุกรายการ จึงห้ามขึ้นว่าเอกสารครบเฉย ๆ
const hasPermitCard = (reqs) => reqs.some(r => r.required && reqCat(r.req_type) === 'permit');

function whyLine(s, reqs, u, consignmentCount) {
  if (!consignmentCount) return 'ยังไม่มีของจาก supplier — เพิ่มเพื่อเริ่มรวบรวมเอกสาร';
  const gaps = openGaps(reqs);
  if (!gaps.length) return hasPermitCard(reqs) ? 'ทุกช่องจับคู่แล้ว · ใบอนุญาตฝากเช็คว่าครอบทุกรายการ' : 'เอกสารครบ รอขั้นตอนถัดไป';
  const first = reqLabel(gaps[0].req_type);
  if (u.urgency === 'urgent') {
    const ft = daysUntil(s.free_time_ends);
    const clockNote = Number.isFinite(ft) && ft <= daysUntil(s.eta)
      ? `free time เหลือ ${ft} วัน` : `ของถึงใน ${u.clock} วัน`;
    return `${first} ยังไม่มา + ${clockNote}`;
  }
  if (u.urgency === 'watch') return `รอ ${gaps.length} ใบ — ใกล้ถึงแล้ว (${u.clock} วัน)`;
  return 'ยังเร็ว กำลังรวบรวมเอกสาร';
}

const ACTION_VERB = { payment_proof: 'แนบ', booking_confirmation: 'จอง',
  declaration: 'ทำ', duty_receipt: 'ชำระอากร →', goods_receipt: 'รับของ' };

function nextAction(s, reqs, consignmentCount) {
  if (!consignmentCount) return '① เพิ่มของจาก supplier — ระบบจะบอกเอกสารที่ต้องใช้ให้เอง';
  const gaps = openGaps(reqs);
  if (!gaps.length) return hasPermitCard(reqs) ? 'ฝากเช็คใบอนุญาตว่าครอบทุกรายการ — แล้วตรวจใบขน/รอของถึง' : 'เอกสารครบ — ตรวจใบขน/รอของถึง';
  const g = gaps[0];
  if (g.status === 'flagged') return `ตรวจ ${reqLabel(g.req_type)} — ${g.conditional_reason ?? 'field ไม่ตรง'}`;
  const verb = ACTION_VERB[g.req_type] ?? 'ตาม';
  return `${verb} ${reqLabel(g.req_type)}`;
}

// ปิดงานแล้ว = จบจริง ไม่ต้องเร่งอีก (ไม่งั้นตู้เก่าโชว์ "ถึงแล้ว 195 วัน" สีแดงน่าตกใจ)
const DONE_STATUS = new Set(['received', 'delivered']);

// shipment แถวจาก listShipments() (มี consignment + document_requirement ซ้อน)
export function toRailVM(s) {
  const reqs = s.document_requirement ?? [];
  const u = computeUrgency(s, reqs);
  const required = reqs.filter(r => r.required);
  const matched = required.filter(r => ['received', 'verified'].includes(r.status));
  const cons = s.consignment ?? [];
  const names = [...new Set(cons.map(c => c.supplier_name))];
  const done = DONE_STATUS.has(s.status);
  return {
    id: s.code, dbId: s.id, raw: s, done,
    label: s.label, mode: s.mode,
    route: [s.origin_port, s.dest_port].filter(Boolean).join(' → ') || '—',
    eta: done ? 'รับของแล้ว' : etaText(s),
    phase: PHASE_OF_STATUS[s.status] ?? s.status,
    // done → โทนเขียวสงบ + ไม่ขยับ; ตัวเลข clock ยังเก็บไว้ใช้เรียง
    urgency: done ? 'done' : u.urgency, clock: u.clock,
    why: done ? 'ปิดงานแล้ว — รับของเข้าคลังครบ' : whyLine(s, reqs, u, cons.length),
    pct: required.length ? Math.round(matched.length / required.length * 100) : 0,
    next: done ? 'จบงานแล้ว' : nextAction(s, reqs, cons.length),
    displayName: s.label || names.join(' · ') || s.code,
    alert: done ? null : buildAlert(s, u),
    consignments: s.consignment ?? [],
    requirements: reqs,
  };
}

function buildAlert(s, u) {
  const ft = daysUntil(s.free_time_ends);
  if (Number.isFinite(ft) && ft <= 7 && u.blockingGaps > 0) {
    const rate = s.storage_per_day ? ` ก่อนเสีย storage ฿${Number(s.storage_per_day).toLocaleString()}/วัน` : '';
    return `Free time เหลือ ${ft} วัน${rate} — ยังขาดเอกสาร ${u.blockingGaps} ใบ`;
  }
  return null;
}

export function sortRail(vms) { return sortByUrgency(vms); }

// ลำดับการ์ด "นิ่ง" — เรียงตามช่วงเวลาที่ต้องใช้ (needed_by) + ชนิดเอกสาร ซึ่งไม่เปลี่ยน
// ตอนใส่ไฟล์ → การ์ดไม่เด้งย้ายที่หลังจับคู่ (พนักงานจำลำดับได้)
const stableReqSort = (a, b) =>
  (NEEDED_BY_RANK[a.needed_by] ?? 9) - (NEEDED_BY_RANK[b.needed_by] ?? 9)
  || String(a.req_type).localeCompare(String(b.req_type))
  || String(a.side ?? '').localeCompare(String(b.side ?? ''));

// board: จัดกลุ่มการ์ดต่อ consignment + ระดับตู้ (requirement LEFT JOIN document)
export function toBoardVM(vm, documents) {
  const docById = Object.fromEntries((documents ?? []).map(d => [d.id, d]));
  const cardOf = (r) => ({
    reqId: r.id, req: r,
    t: reqLabel(r.req_type), k: reqCat(r.req_type),
    s: CARD_STATUS(r),
    f: docById[r.matched_document_id]?.file_name ?? null,
    filePath: docById[r.matched_document_id]?.file_path ?? null,
    docId: r.matched_document_id ?? null,
    // ไม่ใช้สิทธิ์ FTA (data.setFtaWaived) — conditional_reason ยังเป็น "JTEPA → Form …" ที่ compute เขียนทับทุกรอบ
    note: r.waived
      ? (r.req_type === 'certificate_of_origin' ? 'ไม่ใช้สิทธิ์ FTA — ไม่ต้องมี Form' : 'บันทึกว่ารอบนี้ไม่ใช้เอกสารนี้ · เหตุผลอยู่ใน "ประวัติการทำงาน" ด้านล่าง')
      : (r.conditional_reason ?? null),
    side: r.side === 'export' ? 'exp' : r.side === 'import' ? 'imp' : null,
    urgent: vm.urgency === 'urgent' && r.required &&
            ['missing', 'flagged'].includes(r.status) &&
            ['before_ship', 'before_pickup', 'before_clear'].includes(r.needed_by),
  });

  const groups = (vm.consignments ?? []).map((c, i) => {
    const docs = vm.requirements.filter(r => r.consignment_id === c.id).sort(stableReqSort).map(cardOf);
    return {
      consignmentId: c.id,
      kind: `Consignment ${String.fromCharCode(65 + i)}`,
      nm: c.supplier_name, hue: hueFor(c.supplier_name),
      origin: c.origin_country, pos: c.invoice_no || '—',
      docs,
    };
  });
  const shipDocs = vm.requirements.filter(r => !r.consignment_id).sort(stableReqSort).map(cardOf);
  const shipmentDocs = {
    kind: 'Shipment', nm: 'ระดับตู้ / พิธีการ', hue: '#ff5470',
    meta: vm.raw.clearance === 'self' ? 'เดินพิธีเอง' : `broker${vm.raw.forwarder ? ' · ' + vm.raw.forwarder : ''}`,
    docs: shipDocs,
  };
  // tray = ไฟล์ที่ยังไม่จับคู่กับช่องไหน
  const matchedIds = new Set(vm.requirements.map(r => r.matched_document_id).filter(Boolean));
  const tray = (documents ?? []).filter(d => !matchedIds.has(d.id));
  return { groups, shipmentDocs, tray };
}

// CO gate (03_): มี CO required แต่ยังไม่ verified
// จ้าง broker → ส่ง draft ให้ broker ช่วยตรวจได้ (advisory ไม่ block) เพราะ broker ตรวจให้อีกชั้น
// เดินพิธีเอง (self) → เราคือด่านสุดท้าย ต้องตรวจให้ผ่านก่อน (hard block)
export function coGate(vm, certificates) {
  const coReqs = vm.requirements.filter(r =>
    r.req_type === 'certificate_of_origin' && r.required);
  if (!coReqs.length) return { blocked: false, advisory: false, reason: null };
  const pending = coReqs.filter(r => {
    const cert = (certificates ?? []).find(c => c.consignment_id === r.consignment_id);
    return !(cert?.verified);
  });
  if (!pending.length) return { blocked: false, advisory: false, reason: null };
  const isBroker = vm.raw?.clearance !== 'self';
  return isBroker
    ? { blocked: false, advisory: true, pending,
        reason: 'ยังไม่ยืนยัน Form ของเรา — ส่ง draft ให้ broker ช่วยตรวจได้ แต่ยืนยันของเราเองก่อน submit จริง' }
    : { blocked: true, advisory: false, pending,
        reason: 'เดินพิธีเอง — Form/CO ต้องตรวจให้ผ่านก่อน (เราคือด่านสุดท้าย)' };
}
